// src/components/sidebar/SidebarItem.tsx
import React, { useMemo } from "react";
import { Img, Text, Flex } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import colors from "../../utils/colors";

interface SidebarItemProps {
  icon: string;
  activeIcon: string;
  title: string;
  onClick: () => void;
  /** Optional explicit path (e.g. "/admissions"); if provided, used for active matching */
  path?: string;
}

/** Fallback: normalize a display title to a route-friendly slug for segment match */
function titleToSlug(title: string): string {
  const base = (title || "").trim().toLowerCase();
  if (base === "home") return "doctor"; // treat Home as /doctor
  return base.replace(/\s+/g, "-");
}

/** Get the first URL segment from a full path string */
function firstSegment(p: string): string {
  const parts = (p || "/").split("/").filter(Boolean);
  return parts[0] || "";
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  activeIcon,
  title,
  onClick,
  path, // optional
}) => {
  const { primary, dark, lightGrey } = colors as any;
  const location = useLocation();

  // Current page's first segment
  const currentSegment = useMemo(() => {
    const p = location?.pathname || "/";
    const seg = firstSegment(p);
    return seg || (p === "/" ? "doctor" : ""); // consider "/" as /doctor
  }, [location?.pathname]);

  // Expected first segment for this item
  const expectedSegment = useMemo(() => {
    if (typeof path === "string" && path.length > 0) {
      return firstSegment(path);
    }
    return titleToSlug(title);
  }, [path, title]);

  const active = currentSegment === expectedSegment;

  // Safeguard the image src
  const iconSrc = active ? activeIcon || icon : icon || activeIcon;

  return (
    <Flex
      onClick={onClick}
      role="button"
      aria-current={active ? "page" : undefined}
      w="85%"
      gap="16px"
      alignItems="center"
      fontWeight="500"
      fontSize="lg"
      borderRadius={active ? "10px" : undefined}
      color={active ? "white" : dark}
      bg={active ? primary : "transparent"}
      p="12px 24px"
      cursor="pointer"
      _hover={{
        bg: !active ? lightGrey : undefined,
        color: !active ? primary : undefined,
      }}
      transition="background-color 0.2s ease-in-out, color 0.2s ease-in-out"
    >
      <Img
        src={iconSrc}
        boxSize="24px"
        alt={title || "menu item"}
        onError={(e) => {
          // hide broken image icon gracefully
          (e.target as HTMLImageElement).style.visibility = "hidden";
        }}
      />
      <Text noOfLines={1}>{title}</Text>
    </Flex>
  );
};

export default SidebarItem;
